<script setup>
import usePdfGenerator from '@/utils/usePdfGenerator'
import { formatCurrency, formatDecimals } from '@/helpers/stringHelpers'
import { computed, defineProps } from 'vue'

const props = defineProps({
  rows: {
    type: Array,
    required: true
  },
  selectedWeek: {
    type: Number
  },
  tableHeaders: {
    type: Array,
    required: true
  }
})

const HEADER_FILL_COLOR = '#f8f9fb'
const BORDER_COLOR = '#dde0e5'
const BORDER_BOTTOM_WIDTH = 0.2
const BORDER_STYLES = {
  lineWidth: { bottom: BORDER_BOTTOM_WIDTH },
  lineColor: BORDER_COLOR
}

const { generatingPdf, exportPdf } = usePdfGenerator()

const fileName = computed(
  () =>
    `Processing-cost[${
      props.selectedWeek ? 'week-' + props.selectedWeek : 'All Time'
    }].pdf`
)
const title = computed(
  () =>
    `PROCESSING COST SUMMARY - ${
      props.selectedWeek ? 'WEEK ' + props.selectedWeek : 'ALL TIME'
    }`
)

const pdfHeaders = computed(() => {
  const headers = props.tableHeaders.map((h) => {
    const header = { content: h.text }
    if (h.align) header.styles = { halign: h.align }
    return header
  })

  return headers
})

const addBorder = (cells) => {
  return cells.map((cell) => {
    return {
      ...cell,
      styles: {
        ...BORDER_STYLES,
        ...(cell.styles || {})
      }
    }
  })
}

const pdfTableBody = computed(() => {
  return props.rows.map((row) => {
    switch (row.type) {
      case 'sectionHeader':
        return addBorder([
          {
            content: row.label,
            colSpan: 5,
            styles: {
              fontStyle: 'bold',
              halign: 'left',
              fillColor: HEADER_FILL_COLOR
            }
          }
        ])

      case 'item':
        return addBorder([
          {
            content: `   ${row.description}`,
            styles: { halign: 'left' }
          },
          { content: formatDecimals(row.qty, 2) },
          { content: row.unit, styles: { halign: 'center' } },
          { content: formatDecimals(row.unitPrice, 3) },
          { content: formatDecimals(row.amount, 2) }
        ])

      case 'subtotal':
        return addBorder([
          {
            content: row.label,
            colSpan: 4,
            styles: {
              fontStyle: 'bold',
              halign: 'left',
              fillColor: HEADER_FILL_COLOR
            }
          },
          {
            content: `${formatCurrency(row.amount, 2)}`,
            styles: { fontStyle: 'bold', fillColor: HEADER_FILL_COLOR }
          }
        ])

      case 'grandTotal':
        return [
          {
            content: row.label,
            colSpan: 5,
            styles: {
              fontStyle: 'bold',
              halign: 'right'
            }
          }
        ]

      default:
        return []
    }
  })
})

const generatePdf = () => {
  exportPdf(
    [],
    title.value,
    pdfTableBody.value,
    pdfHeaders.value,
    fileName.value,
    {
      headFillColor: '#343a40',
      halign: 'right',
      theme: 'plain'
    }
  )
}
</script>

<template>
  <v-btn
    @click="generatePdf"
    small
    color="warning"
    :loading="generatingPdf"
    class="ms-auto"
  >
    <font-awesome-icon icon="fas fa-file-export" class="mr-2" />Export</v-btn
  >
</template>
